import { useState, useEffect } from "react";
import { TRIGGER_OPTIONS, ACTION_OPTIONS } from "@/pages/admin/automations/automationConfig";

export interface AutomationRule {
  id: string;
  tenant_id: string;
  trigger: string;
  action: string;
  label: string;
  description: string | null;
  is_active: boolean;
  created_at: string;
}

const blank = (): Omit<AutomationRule, "id" | "tenant_id" | "created_at"> => ({
  trigger:     TRIGGER_OPTIONS[0].value,
  action:      ACTION_OPTIONS[0].value,
  label:       "",
  description: null,
  is_active:   true,
});

interface Props {
  rule: AutomationRule | null;
  isNew: boolean;
  onClose: () => void;
  onSave: (data: Omit<AutomationRule, "id" | "tenant_id" | "created_at">) => void;
}

export default function AutomationModal({ rule, isNew, onClose, onSave }: Props) {
  const [form, setForm] = useState(blank());

  useEffect(() => {
    if (rule) {
      setForm({
        trigger:     rule.trigger,
        action:      rule.action,
        label:       rule.label,
        description: rule.description,
        is_active:   rule.is_active,
      });
    } else {
      setForm(blank());
    }
  }, [rule]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.label.trim()) return;
    onSave({ ...form, description: form.description || null });
  };

  const selectedTrigger = TRIGGER_OPTIONS.find((t) => t.value === form.trigger);
  const selectedAction  = ACTION_OPTIONS.find((a) => a.value === form.action);

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-white border border-slate-200 rounded-2xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2.5">
            <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#C8A96E]/10 text-[#C8A96E]">
              <i className="ri-flashlight-line text-sm"></i>
            </span>
            <h2 className="text-[#0f172a] font-semibold text-base">
              {isNew ? "New Automation Rule" : "Edit Rule"}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-all cursor-pointer"
          >
            <i className="ri-close-line"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Label */}
          <div>
            <label className="text-slate-500 text-xs mb-1.5 block">Rule Name *</label>
            <input
              name="label"
              value={form.label}
              onChange={handleChange}
              required
              placeholder="e.g. Notify admin when job completes"
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#0f172a] placeholder-slate-300 outline-none focus:border-[#C8A96E]/50 transition-colors"
            />
          </div>

          {/* Trigger → Action preview */}
          <div className="flex items-center gap-3 px-4 py-3 bg-slate-50 rounded-xl border border-slate-200">
            <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${selectedTrigger?.color ?? "bg-slate-100 text-slate-400"}`}>
              <i className={`${selectedTrigger?.icon ?? "ri-flashlight-line"} text-xs`}></i>
              {selectedTrigger?.label ?? form.trigger}
            </div>
            <div className="flex-1 flex items-center justify-center">
              <i className="ri-arrow-right-line text-slate-300 text-sm"></i>
            </div>
            <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${selectedAction?.color ?? "bg-slate-100 text-slate-400"}`}>
              <i className={`${selectedAction?.icon ?? "ri-play-circle-line"} text-xs`}></i>
              {selectedAction?.label ?? form.action}
            </div>
          </div>

          {/* Trigger */}
          <div>
            <label className="text-slate-500 text-xs mb-1.5 block">Trigger Event</label>
            <select
              name="trigger"
              value={form.trigger}
              onChange={handleChange}
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#0f172a] outline-none focus:border-[#C8A96E]/50 transition-colors cursor-pointer appearance-none"
            >
              {TRIGGER_OPTIONS.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>

          {/* Action */}
          <div>
            <label className="text-slate-500 text-xs mb-1.5 block">Action to Run</label>
            <select
              name="action"
              value={form.action}
              onChange={handleChange}
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#0f172a] outline-none focus:border-[#C8A96E]/50 transition-colors cursor-pointer appearance-none"
            >
              {ACTION_OPTIONS.map((a) => (
                <option key={a.value} value={a.value}>{a.label}</option>
              ))}
            </select>
          </div>

          {/* Description */}
          <div>
            <label className="text-slate-500 text-xs mb-1.5 block">
              Description <span className="text-slate-300">(optional)</span>
            </label>
            <textarea
              name="description"
              value={form.description ?? ""}
              onChange={handleChange}
              rows={2}
              maxLength={300}
              placeholder="What does this rule do?"
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#0f172a] placeholder-slate-300 outline-none focus:border-[#C8A96E]/50 transition-colors resize-none"
            />
          </div>

          {/* Active toggle */}
          <div className="flex items-center justify-between px-4 py-3 bg-slate-50 rounded-xl border border-slate-200">
            <div>
              <p className="text-[#0f172a] text-sm font-medium">Active</p>
              <p className="text-slate-400 text-xs">Enable this rule immediately</p>
            </div>
            <button
              type="button"
              onClick={() => setForm((prev) => ({ ...prev, is_active: !prev.is_active }))}
              className={`relative w-11 h-6 rounded-full transition-all cursor-pointer ${
                form.is_active ? "bg-[#C8A96E]" : "bg-slate-200"
              }`}
            >
              <span
                className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${
                  form.is_active ? "left-[22px]" : "left-0.5"
                }`}
              />
            </button>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-slate-500 hover:text-slate-700 border border-slate-200 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-5 py-2 bg-[#C8A96E] hover:bg-[#b8965a] text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              {isNew ? "Create Rule" : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}